// Moteur conditionnel des formulaires : copie TypeScript FIDÈLE de
// « VisaTravelApp backend/formulairesPublics/moteur.js » (source et autorité des règles de visibilité / validation).
// Toute divergence de comportement avec moteur.js est un défaut : ce fichier est rejoué sur les mêmes vecteurs
// (./cas-tests.json, copie identique) par visibilite.test.ts. Module PUR : ni React, ni axios, ni réseau.
// Copie conforme de « adminvisastravelapp/src/formulaires/visibilite.ts » (lot 4b) : les trois exemplaires (backend
// client, admin, ici) doivent rester identiques hors cet en-tête.
// Sert à la PAGE PUBLIQUE /formulaire-public/:jeton (lot 6) : visibilité des sections / questions et validation
// locales sur les valeurs en cours de saisie, avant l'enregistrement ; le backend recalcule tout à chaque écriture
// et reste l'autorité (visibles / sections_visibles / manquantes renvoyés, 422 à la soumission).
//
// Règles (plan §4) :
// 1. Une condition vise toujours une question antérieure (vérifié côté admin par schema.js) :
//    l'évaluation se fait en UNE passe, dans l'ordre du document.
// 2. Une section est visible si elle n'a pas de condition ou si sa condition est vraie.
// 3. Une question est visible si sa section est visible ET (pas de condition ou condition vraie).
// 4. Cible invisible = condition FAUSSE quel que soit l'opérateur (même different / vide / …).
// 5. Comparaison sur String(v).trim() ; « rempli » = ≥ 1 fichier, tableau non vide ou chaîne trimée non vide.
//
// Typage : les structures internes (index, questions, sections) sont celles de ./types ; les ENTRÉES restent du JSON
// de confiance, éventuellement partiel ou nul, exactement comme moteur.js les tolère (sections / questions absentes ou
// mal typées ignorées, valeurs / fichiers null tolérés). `nettoyer` reçoit donc des valeurs brutes
// (Record<string, unknown>) et rend des valeurs propres (Record<string, string | string[]>).
import type { Condition, FichierReponse, Question, Section, TypeQuestion, ValeurReponse } from './types';
import { TYPES, OPERATEURS, OPERATEURS_PAR_TYPE } from './types';

export { TYPES, OPERATEURS, OPERATEURS_PAR_TYPE };

// Définition telle que le moteur la lit : une Definition complète (./types) convient toujours ; un objet partiel
// (voire {} ou null) est toléré comme dans moteur.js — `sections` est contrôlé à l'exécution par `indexer`.
export type DefinitionSource = { sections?: unknown } | null | undefined;
// Valeurs / fichiers bruts (JSON de la base ou état local) ; null toléré
export type ValeursSource = Record<string, unknown> | null | undefined;
export type FichiersSource = Record<string, unknown> | null | undefined;
// Valeurs propres rendues par `nettoyer` : chaîne, ou tableau de chaînes pour choix_multiple
export type ValeursPropres = Record<string, ValeurReponse>;

export interface EntreeIndex { q: Question; section: Section; iSection: number; iQuestion: number }
export interface Index { questions: Map<string, EntreeIndex>; sections: Section[] }
export interface ErreurReponse { question: string; message: string }
export interface ResultatVisibilite { visibles: string[]; sectionsVisibles: string[] }
export interface ResultatPurge { valeurs: Record<string, unknown>; fichiers: Record<string, FichierReponse[]>; fichiersASupprimer: string[] }

// Longueur maximale d'une valeur stockée, par type (borne de sécurité de `nettoyer`).
// texte / texte_long : plafond du type ; le `max` propre à la question est contrôlé par `valider`.
export const MAX_PAR_TYPE: Partial<Record<TypeQuestion, number>> = {
  texte: 2000, texte_long: 5000, nombre: 40, date: 10, oui_non: 40, choix: 40, choix_multiple: 40,
};

// Mêmes expressions que moteur.js (REGEX_NOMBRE / REGEX_DATE_ISO valent REGEX.numerique / REGEX.dateIso de ./types)
export const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const REGEX_TELEPHONE = /^[+0-9 ().-]{6,20}$/;
export const REGEX_NOMBRE = /^-?\d+(\.\d+)?$/;
export const REGEX_DATE_ISO = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const estObjet = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const chaine = (v: unknown): string => (v == null ? '' : String(v)).trim();
const enTableau = (v: unknown): unknown[] => (Array.isArray(v) ? v : v == null || chaine(v) === '' ? [] : [v]);
const valeursOptions = (q: Question): string[] => (Array.isArray(q.options) ? q.options : []).map((o) => chaine(o?.valeur));
const listeFichiers = (fichiers: FichiersSource, id: string): unknown[] =>
  estObjet(fichiers) && Array.isArray(fichiers[id]) ? (fichiers[id] as unknown[]) : [];

// Date ISO aaaa-mm-jj réellement valide (pas de 30 février)
const dateIsoValide = (s: string): boolean => {
  if (!REGEX_DATE_ISO.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};

// ---------------------------------------------------------------------------
// Indexation
// ---------------------------------------------------------------------------

// def -> { questions: Map id -> { q, section, iSection, iQuestion }, sections: [...] }
// Tolérant : sections / questions absentes ou non tableaux sont ignorées ; premier id gagne en cas de doublon.
// `sections` est le tableau reçu tel quel (JSON de confiance) : les parcours gardent les gardes de moteur.js.
export const indexer = (def: DefinitionSource): Index => {
  const sections: Section[] = def && Array.isArray(def.sections) ? (def.sections as Section[]) : [];
  const questions = new Map<string, EntreeIndex>();
  sections.forEach((section, iSection) => {
    (section && Array.isArray(section.questions) ? section.questions : []).forEach((q, iQuestion) => {
      if (q && typeof q.id === 'string' && !questions.has(q.id)) questions.set(q.id, { q, section, iSection, iQuestion });
    });
  });
  return { questions, sections };
};

// ---------------------------------------------------------------------------
// Évaluation
// ---------------------------------------------------------------------------

// Une question est « remplie » : fichier -> ≥ 1 fichier ; tableau -> longueur > 0 ; sinon chaîne trimée non vide.
export const estRempli = (q: Question | null | undefined, valeurs: ValeursSource, fichiers: FichiersSource): boolean => {
  if (!q) return false;
  if (q.type === 'fichier') return listeFichiers(fichiers, q.id).length > 0;
  const v = estObjet(valeurs) ? valeurs[q.id] : undefined;
  if (Array.isArray(v)) return v.length > 0;
  return chaine(v) !== '';
};

// cond = { question, op, valeur? } ; cible = question visée (objet) ; visiblesSet = Set des ids déjà visibles.
// Règle 4 : cible absente de visiblesSet -> false quel que soit l'opérateur.
export const evaluerCondition = (
  cond: Condition | null | undefined, cible: Question | null | undefined, valeurs: ValeursSource, fichiers: FichiersSource,
  visiblesSet: Set<string> | null | undefined
): boolean => {
  if (!cond || !cible || !visiblesSet || !visiblesSet.has(cond.question)) return false;
  const rempli = estRempli(cible, valeurs, fichiers);
  const brut = estObjet(valeurs) ? valeurs[cible.id] : undefined;
  const v = chaine(brut);
  const attendu = chaine(cond.valeur);
  const multiple = cible.type === 'choix_multiple';
  const liste: string[] = Array.isArray(cond.valeur) ? cond.valeur.map(chaine) : [];

  switch (cond.op) {
    case 'rempli': return rempli;
    case 'vide': return !rempli;
    case 'egal': return v === attendu;
    case 'different': return v !== attendu;
    case 'contient':
    case 'contient_pas': {
      let contient: boolean;
      if (multiple) contient = enTableau(brut).map(chaine).includes(attendu);
      else contient = attendu !== '' && v.toLowerCase().includes(attendu.toLowerCase());
      return cond.op === 'contient' ? contient : !contient;
    }
    case 'parmi':
    case 'pas_parmi': {
      let parmi: boolean;
      if (multiple) parmi = enTableau(brut).map(chaine).some((x) => liste.includes(x));
      else parmi = liste.includes(v);
      return cond.op === 'parmi' ? parmi : !parmi;
    }
    default: return false;
  }
};

// Une passe dans l'ordre du document -> { visibles: string[], sectionsVisibles: string[] }
export const questionsVisibles = (def: DefinitionSource, valeurs: ValeursSource, fichiers: FichiersSource): ResultatVisibilite => {
  const index = indexer(def);
  const visiblesSet = new Set<string>();
  const visibles: string[] = [];
  const sectionsVisibles: string[] = [];
  const conditionVraie = (cond: Condition | null | undefined): boolean => {
    const cible = cond ? index.questions.get(cond.question) : undefined;
    return evaluerCondition(cond, cible ? cible.q : null, valeurs, fichiers, visiblesSet);
  };
  index.sections.forEach((section) => {
    if (!section) return;
    const sectionVisible = !section.condition || conditionVraie(section.condition);
    if (sectionVisible && typeof section.id === 'string') sectionsVisibles.push(section.id);
    (Array.isArray(section.questions) ? section.questions : []).forEach((q) => {
      if (!q || typeof q.id !== 'string' || visiblesSet.has(q.id)) return;
      if (sectionVisible && (!q.condition || conditionVraie(q.condition))) {
        visiblesSet.add(q.id);
        visibles.push(q.id);
      }
    });
  });
  return { visibles, sectionsVisibles };
};

// Questions obligatoires visibles non remplies (info exclu), dans l'ordre du document.
export const manquantes = (def: DefinitionSource, valeurs: ValeursSource, fichiers: FichiersSource, visibles: string[] | null | undefined): string[] => {
  const visiblesSet = new Set<string>(visibles || []);
  const res: string[] = [];
  indexer(def).questions.forEach(({ q }) => {
    if (q.type === 'info' || !q.obligatoire || !visiblesSet.has(q.id)) return;
    if (!estRempli(q, valeurs, fichiers)) res.push(q.id);
  });
  return res;
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const messageNombre = (q: Question): string => {
  const min = typeof q.min === 'number' ? q.min : null;
  const max = typeof q.max === 'number' ? q.max : null;
  if (min !== null && max !== null) return `Nombre attendu entre ${min} et ${max}`;
  if (min !== null) return `Nombre attendu supérieur ou égal à ${min}`;
  if (max !== null) return `Nombre attendu inférieur ou égal à ${max}`;
  return 'Nombre attendu';
};

// Longueur maximale d'un texte : `max` de la question borné par le plafond du type.
const maxTexte = (q: Question): number => {
  const plafond = MAX_PAR_TYPE[q.type] ?? 2000;
  return typeof q.max === 'number' && q.max > 0 && q.max < plafond ? q.max : plafond;
};

// Première erreur d'une question VISIBLE et remplie (null si aucune)
const erreurQuestion = (q: Question, valeurs: Record<string, unknown>, fichiers: FichiersSource): string | null => {
  const brut = valeurs[q.id];
  const v = chaine(brut);
  switch (q.type) {
    case 'fichier': {
      const max = typeof q.max_fichiers === 'number' && q.max_fichiers > 0 ? q.max_fichiers : 1;
      if (listeFichiers(fichiers, q.id).length > max) return `${max} fichier${max > 1 ? 's' : ''} maximum`;
      return null;
    }
    case 'choix':
      return valeursOptions(q).includes(v) ? null : 'Valeur non autorisée';
    case 'oui_non':
      return v === 'oui' || v === 'non' ? null : 'Valeur non autorisée';
    case 'choix_multiple': {
      if (!Array.isArray(brut)) return 'Valeur non autorisée';
      const options = valeursOptions(q);
      return brut.every((x) => options.includes(chaine(x))) ? null : 'Valeur non autorisée';
    }
    case 'nombre': {
      if (!REGEX_NOMBRE.test(v)) return messageNombre(q);
      const n = Number(v);
      if (typeof q.min === 'number' && n < q.min) return messageNombre(q);
      if (typeof q.max === 'number' && n > q.max) return messageNombre(q);
      return null;
    }
    case 'date':
      return dateIsoValide(v) ? null : 'Date invalide';
    case 'texte':
    case 'texte_long': {
      if (q.type === 'texte' && q.format === 'email' && !REGEX_EMAIL.test(v)) return 'E-mail invalide';
      if (q.type === 'texte' && q.format === 'telephone' && !REGEX_TELEPHONE.test(v)) return 'Téléphone invalide';
      const max = maxTexte(q);
      if (v.length > max) return `${max} caractères maximum`;
      return null;
    }
    default:
      return null;
  }
};

// -> [{ question, message }] : une seule erreur par question, questions visibles uniquement, ordre du document.
export const valider = (def: DefinitionSource, valeurs: ValeursSource, fichiers: FichiersSource, visibles: string[] | null | undefined): ErreurReponse[] => {
  const vals: Record<string, unknown> = estObjet(valeurs) ? valeurs : {};
  const visiblesSet = new Set<string>(visibles || []);
  const erreurs: ErreurReponse[] = [];
  indexer(def).questions.forEach(({ q }) => {
    if (q.type === 'info' || !visiblesSet.has(q.id)) return;
    const rempli = estRempli(q, vals, fichiers);
    if (!rempli) {
      if (q.obligatoire) erreurs.push({ question: q.id, message: 'Réponse obligatoire' });
      return;
    }
    const message = erreurQuestion(q, vals, fichiers);
    if (message) erreurs.push({ question: q.id, message });
  });
  return erreurs;
};

// ---------------------------------------------------------------------------
// Nettoyage / purge
// ---------------------------------------------------------------------------

// Valeurs propres : ids connus hors info / fichier ; choix_multiple -> string[] (seuls les string / number sont
// gardés) ; autres -> String(v) bornée au plafond du type ; null / undefined et objets (jamais « [object Object] »
// en base) ignorés. Ne modifie pas l'objet reçu. Même logique que moteur.js (backend client).
export const nettoyer = (def: DefinitionSource, valeurs: ValeursSource): ValeursPropres => {
  const vals: Record<string, unknown> = estObjet(valeurs) ? valeurs : {};
  const res: ValeursPropres = {};
  indexer(def).questions.forEach(({ q }) => {
    if (q.type === 'info' || q.type === 'fichier') return;
    const v = vals[q.id];
    if (v == null) return;
    const plafond = MAX_PAR_TYPE[q.type] ?? 2000;
    if (q.type === 'choix_multiple') {
      res[q.id] = (Array.isArray(v) ? v : [v])
        .filter((x) => typeof x === 'string' || typeof x === 'number')
        .map((x) => String(x).slice(0, plafond));
    } else {
      if (typeof v === 'object') return; // objet ou tableau sur une question scalaire : ignoré
      res[q.id] = String(v).slice(0, plafond);
    }
  });
  return res;
};

// À la soumission : retire valeurs et fichiers des questions invisibles (ou inconnues de la définition).
// fichiersASupprimer = noms disque des fichiers retirés (à effacer par la route). Ne modifie pas les objets reçus.
// Les listes de fichiers conservées sont transmises telles quelles (JSON de confiance : FichierReponse[]).
export const purger = (def: DefinitionSource, valeurs: ValeursSource, fichiers: FichiersSource, visibles: string[] | null | undefined): ResultatPurge => {
  const index = indexer(def);
  const visiblesSet = new Set<string>(visibles || []);
  const vals: Record<string, unknown> = estObjet(valeurs) ? valeurs : {};
  const fich: Record<string, unknown> = estObjet(fichiers) ? fichiers : {};
  const res: ResultatPurge = { valeurs: {}, fichiers: {}, fichiersASupprimer: [] };
  Object.keys(vals).forEach((id) => {
    if (index.questions.has(id) && visiblesSet.has(id)) res.valeurs[id] = vals[id];
  });
  Object.keys(fich).forEach((id) => {
    const liste: unknown[] = Array.isArray(fich[id]) ? (fich[id] as unknown[]) : [];
    if (index.questions.has(id) && visiblesSet.has(id)) res.fichiers[id] = liste as FichierReponse[];
    else liste.forEach((f) => { if (estObjet(f) && typeof f.nom === 'string') res.fichiersASupprimer.push(f.nom); });
  });
  return res;
};
